import { Injectable, inject } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { Observable, map } from 'rxjs';
import {
  DnaSportGuideline,
  DnaSportGuidelineExercise,
  DnaSportGuidelineExerciseQueriesFindByUserArgs,
  DnaSportGuidelineQueriesFindByUserArgs
} from '@interfaces/schema';

/**
 * Health guideline service.
 */
@Injectable()
export class ReportActiveHGService {
  private readonly apollo = inject(Apollo);

  /**
   * Fetch guideline.
   *
   * @param {DnaSportGuidelineQueriesFindByUserArgs} variables Variables.
   * @returns {Observable<DnaSportGuideline | null>}
   */
  fetchGuideline(
    variables: DnaSportGuidelineQueriesFindByUserArgs
  ): Observable<DnaSportGuideline | null> {
    const query = gql`
      query findSportGuideline($include: [String]) {
        DNA {
          SportGuideline {
            findByUser(include: $include) {
              id
              dnaUserId
              dnaReportId
              weight
              height
              medicalHistory
              fitnessGoals
              injuryHistory
              bodyMassIndex
              availableExerciseTimePerSession
              recommendedMETPerWeek
              isMyExerciseRelevant
              isGeneralExerciseRelevant
              sGuidelineSummary {
                id
                sportGuidelineId
                type
                position
                description
                createdAt
                updatedAt
              }
              sGuidelineExercise {
                id
                sportGuidelineId
                exerciseName
                exerciseNameId
                exerciseType
                exerciseDescription
                exerciseDescriptionId
                exerciseIcon
                exerciseMETValue
                type
              }
            }
          }
        }
      }
    `;
    return this.apollo
      .query({
        query,
        variables
      })
      .pipe(
        map(
          (response) =>
            (response.data as any)?.DNA?.SportGuideline
              ?.findByUser as DnaSportGuideline
        )
      );
  }

  /**
   * Fetch guideline exercises.
   *
   * @param {DnaSportGuidelineExerciseQueriesFindByUserArgs} variables Variables.
   * @returns {Observable<DnaSportGuidelineExercise[] | null>}
   */
  fetchGuidelineExercises(
    variables: DnaSportGuidelineExerciseQueriesFindByUserArgs
  ): Observable<Array<DnaSportGuidelineExercise> | null> {
    const query = gql`
      query findGuidelineExercises($include: [String]) {
        DNA {
          SportGuideline {
            Exercise {
              findByUser(include: $include) {
                id
                sportGuidelineId
                exerciseName
                exerciseDescription
                exerciseIcon
                exerciseType
                exerciseMETValue
                type
                sGuidelineExerciseInsight {
                  id
                  description
                  type
                  position
                  createdAt
                }
              }
            }
          }
        }
      }
    `;
    return this.apollo
      .query({
        query,
        variables
      })
      .pipe(
        map(
          (response) =>
            (response.data as any)?.DNA?.SportGuideline?.Exercise
              ?.findByUser as Array<DnaSportGuidelineExercise>
        )
      );
  }
}
